import React from "react";
import {useLoaderData} from "@remix-run/react";
import {json} from "@remix-run/node";
import type {LinksFunction, MetaFunction, LoaderFunction} from "@remix-run/node";
import type {OfferBannerProps} from "@bettorsignals/ui";
import {Breadcrumbs, OfferBanner, WithBackgroundGradient, WaitingBanner} from "@bettorsignals/ui";
import {Collection, isOffer} from "@bettorsignals/api";
import type {Offer} from "@bettorsignals/api";
import {LinkTo} from "~/utils/LinkTo";
import {$api} from "~/api";

interface LoaderData {
    offerBanners: OfferBannerProps[];
}

export const links: LinksFunction = () => {
    return [{rel: "canonical", href: `${LinkTo.fullPageUrl()}${LinkTo.bonusesPage()}`}];
};

export const loader: LoaderFunction = async ({request: {headers}}) => {
    // These headers are provided by CloudFront, so will be nullish on localhost
    const userCountry = headers.get("CloudFront-Viewer-Country");
    const userRegion = headers.get("CloudFront-Viewer-Country-Region");

    const offersResponse = await $api.items(Collection.Offers).readByQuery({
        fields: ["*", "regions.region.country.code", "regions.region.regionCode"]
    });

    const geotargetedOffers = (offersResponse.data ?? []).filter((offer): offer is Offer => {
        const isValidOffer = isOffer(offer);

        const isOfferWhitelisted = offer.regions.some(
            (junction: any) =>
                (junction.region.country.code === "*" ||
                    junction.region.country.code === userCountry) &&
                (junction.region.regionCode === "*" || junction.region.regionCode === userRegion)
        );

        return isValidOffer && isOfferWhitelisted;
    });

    const linkTo = new LinkTo(process.env.API_URL!);
    const offerBanners: OfferBannerProps[] = geotargetedOffers.map((offer) => ({
        ...offer,
        url: LinkTo.offer(offer.id),
        logoRectangle: offer.logoRectangle
            ? linkTo.asset(offer.logoRectangle, {
                  seoFriendlyName: "Offer-logo"
              })
            : null
    }));

    return json<LoaderData>({
        offerBanners
    });
};

export const meta: MetaFunction = () => {
    return {
        title: "Bonuses | BettorSignals.com",
        description: "Browse the best online sportsbook offers & bonuses."
    };
};

export default function Bonuses() {
    const {offerBanners} = useLoaderData<LoaderData>();

    return (
        <WithBackgroundGradient>
            <div className="relative mx-auto w-screen max-w-bs px-4 xl:px-0">
                <main className="mt-8 pb-16 md:mt-[26px]">
                    <Breadcrumbs
                        pages={[
                            {
                                name: "Home",
                                href: LinkTo.homePage()
                            },

                            {
                                name: "Bonuses",
                                href: LinkTo.blogPage()
                            }
                        ]}
                    />

                    <h1 className="mt-8 mb-7 text-3xl font-bold leading-9 text-gray-900">
                        Bonuses
                    </h1>

                    {offerBanners.length === 0 ? (
                        <div className="mt-10 flex h-[567px] flex-col justify-start md:mt-3.5 md:h-[486px]">
                            <WaitingBanner
                                buttonText="Give them a PUSH"
                                contents={[
                                    {
                                        text: "Aww, at the moment we don't have any bonuses available in your region...",
                                        className: "text-2xl"
                                    },
                                    {
                                        text: "Our team is working hard to have something ready for your next visit!",
                                        className: "mt-3 text-2xl text-bs-gradient-primary"
                                    }
                                ]}
                            />
                        </div>
                    ) : (
                        <div className="mb-[100px] flex flex-col gap-[30px]">
                            {offerBanners.map((offer) => (
                                <OfferBanner {...offer} key={offer.id} />
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </WithBackgroundGradient>
    );
}
